import type { FC, ReactNode } from 'react';
import { Circle, G, Path, Svg } from '../brand/primitives';
import type { StatusIconComponent, StatusIconProps } from './types';

const make = (name: string, render: (c: string) => ReactNode): StatusIconComponent => {
  const Component: FC<StatusIconProps> = ({ size = 16, color }) => (
    <Svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      {render(color)}
    </Svg>
  );
  Component.displayName = name;
  return Component;
};

export const LiveStatusIcon = make('LiveStatusIcon', (c) => (
  <Circle cx={8} cy={8} r={5} fill={c} />
));

export const CheckStatusIcon = make('CheckStatusIcon', (c) => (
  <Path
    d="M3 8 L7 12 L13 4"
    stroke={c}
    strokeWidth={2.4}
    strokeLinecap="round"
    strokeLinejoin="round"
    fill="none"
  />
));

export const CrossStatusIcon = make('CrossStatusIcon', (c) => (
  <Path
    d="M4 4 L12 12 M12 4 L4 12"
    stroke={c}
    strokeWidth={2.4}
    strokeLinecap="round"
    fill="none"
  />
));

export const FlameStatusIcon = make('FlameStatusIcon', (c) => (
  <Path d="M8 1 C 5 4 3 7 3 10 A 5 5 0 0 0 13 10 C 13 7 11 5 10 3 L 9 5 L 8 1 Z" fill={c} />
));

export const BoltStatusIcon = make('BoltStatusIcon', (c) => (
  <Path d="M9 1 L4 9 H8 L7 15 L12 7 H8 Z" fill={c} />
));

export const AlertStatusIcon = make('AlertStatusIcon', (c) => (
  <G>
    <Path d="M8 1 L15 14 H1 Z" fill={c} />
    <Path d="M8 6 V10 M8 12 V12.5" stroke="#fff" strokeWidth={1.6} strokeLinecap="round" />
  </G>
));

export const InfoStatusIcon = make('InfoStatusIcon', (c) => (
  <G>
    <Circle cx={8} cy={8} r={6.5} fill={c} />
    <Path d="M8 7 V11 M8 5 V5.5" stroke="#fff" strokeWidth={1.6} strokeLinecap="round" />
  </G>
));

export interface VerifiedStatusIconProps extends StatusIconProps {
  /**
   * Stroke colour of the inner check mark. Defaults to white (the F02 spec).
   * Dark-palette consumers whose `info` fill is light may pass a darker token
   * colour (e.g. `t.color.surface`) for contrast.
   */
  readonly checkColor?: string;
}

// Hand-rolled (not `make`) because of the extra `checkColor` prop. Stays
// hook-free so it renders identically on web (plain <svg>) and RN (Metro picks
// `primitives.native`).
const VerifiedStatusIconImpl: FC<VerifiedStatusIconProps> = ({
  size = 16,
  color,
  checkColor = '#fff',
}) => (
  <Svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <G>
      <Path
        d="M8 1 L9.8 2.7 L12 2 L12.5 4.5 L15 5 L14 7.4 L15 10 L12.5 11 L12 13.4 L9.8 12.7 L8 14.4 L6.2 12.7 L4 13.4 L3.5 11 L1 10 L2 7.4 L1 5 L3.5 4.5 L4 2 L6.2 2.7 Z"
        fill={color}
      />
      <Path
        d="M5 7.5 L7.2 9.7 L11.4 5.5"
        stroke={checkColor}
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </G>
  </Svg>
);
VerifiedStatusIconImpl.displayName = 'VerifiedStatusIcon';

export const VerifiedStatusIcon: FC<VerifiedStatusIconProps> = VerifiedStatusIconImpl;

// ─── Registry (dynamic-name lookup) ───────────────────────────────────────

export const STATUS_REGISTRY = {
  live: LiveStatusIcon,
  check: CheckStatusIcon,
  cross: CrossStatusIcon,
  flame: FlameStatusIcon,
  bolt: BoltStatusIcon,
  alert: AlertStatusIcon,
  info: InfoStatusIcon,
  verified: VerifiedStatusIcon,
} as const satisfies Record<string, StatusIconComponent>;

export type StatusIconName = keyof typeof STATUS_REGISTRY;
export const STATUS_ICON_NAMES: readonly StatusIconName[] = Object.keys(
  STATUS_REGISTRY,
) as StatusIconName[];

export interface StatusIconWrapperProps extends StatusIconProps {
  readonly name: StatusIconName;
}

export const StatusIcon: FC<StatusIconWrapperProps> = ({ name, ...rest }) => {
  const Component = STATUS_REGISTRY[name];
  if (!Component) return null;
  return <Component {...rest} />;
};
